import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  CheckCircleIcon,
  ClockIcon,
  PlayIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { useOrganization } from '../agenticsaas/hooks/useOrganization';
import OnboardingWizard from '../components/onboarding/OnboardingWizard';
import {
  deriveStepComplete,
  firstIncompleteStep,
  ONBOARDING_STEPS,
  readStepRuns,
} from '../components/onboarding/onboardingSteps';
import { fetchOnboardingManifest, fetchOnboardingStatus } from '../services/onboardingApi';

function formatLastRun(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export default function SettingsOnboardingPage({ embedded = false }) {
  const { canManageOrganization, loading: orgLoading } = useOrganization();
  const [status, setStatus] = useState(null);
  const [manifest, setManifest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeStep, setActiveStep] = useState(null);
  const [runVersion, setRunVersion] = useState(0);

  const organizationId = status?.organization_id;

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const nextStatus = await fetchOnboardingStatus();
      setStatus(nextStatus);
      if (nextStatus?.organization_id) {
        const nextManifest = await fetchOnboardingManifest(nextStatus.organization_id).catch(() => null);
        setManifest(nextManifest);
      }
    } catch {
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const stepRuns = useMemo(
    () => readStepRuns(organizationId),
    [organizationId, runVersion]
  );

  const stepStates = useMemo(
    () => ONBOARDING_STEPS.map((step) => ({
      ...step,
      complete: deriveStepComplete(step.id, status, manifest, stepRuns),
      lastRun: stepRuns[step.id]?.at || null,
      runCount: stepRuns[step.id]?.count || 0,
    })),
    [status, manifest, stepRuns]
  );

  const completedCount = stepStates.filter((s) => s.complete).length;

  const handleStepFinished = useCallback(() => {
    setRunVersion((v) => v + 1);
    loadData();
  }, [loadData]);

  const handleRunAll = () => {
    const next = firstIncompleteStep(status, manifest, stepRuns);
    setActiveStep(next);
  };

  if (orgLoading || loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (!canManageOrganization) {
    return (
      <div className="max-w-lg mx-auto py-16 px-4 text-center">
        <h1 className="text-xl font-semibold text-gray-900">Owner or admin access required</h1>
        <p className="mt-2 text-sm text-gray-600">
          Onboarding setup is limited to organization owners and admins. Contact your administrator if you need help.
        </p>
      </div>
    );
  }

  const wizardStep = activeStep;

  return (
    <div className={embedded ? '' : 'max-w-4xl mx-auto'}>
      <div className={embedded ? 'mb-6' : 'mb-8'}>
        <div className="flex items-start gap-3">
          {!embedded && (
            <div className="flex-shrink-0 w-11 h-11 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center">
              <SparklesIcon className="w-6 h-6" />
            </div>
          )}
          <div>
            <h2 className={embedded ? 'text-lg font-semibold text-gray-900' : 'text-2xl font-bold text-gray-900'}>
              Onboarding
            </h2>
            <p className="mt-1 text-sm text-gray-600">
              Run setup steps manually if you skipped signup onboarding, need to upload data, or want to re-run the guided demo.
              Progress is remembered per organization.
            </p>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-3">
          <span className="inline-flex items-center gap-1.5 text-sm text-gray-700 bg-white border border-gray-200 rounded-full px-3 py-1">
            <CheckCircleIcon className="w-4 h-4 text-green-600" />
            {completedCount} of {ONBOARDING_STEPS.length} steps done
          </span>
          {status?.onboarding_completed && (
            <span className="inline-flex items-center text-sm text-green-800 bg-green-50 border border-green-200 rounded-full px-3 py-1">
              Onboarding marked complete
            </span>
          )}
          <button
            type="button"
            onClick={handleRunAll}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg px-4 py-2"
          >
            <PlayIcon className="w-4 h-4" />
            Run remaining steps
          </button>
        </div>
      </div>

      <div className="space-y-3 mb-8">
        {stepStates.map((step) => (
          <div
            key={step.id}
            className={`bg-white border rounded-xl shadow-sm overflow-hidden ${
              step.complete ? 'border-green-200' : 'border-gray-200'
            }`}
          >
            <div className="flex items-center gap-4 p-4">
              <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                step.complete ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
              }`}
              >
                {step.complete ? <CheckCircleIcon className="w-6 h-6" /> : <span className="text-sm font-semibold">{step.step}</span>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h2 className="text-sm font-semibold text-gray-900">{step.title}</h2>
                  {step.optional && (
                    <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">Optional</span>
                  )}
                  {step.complete && (
                    <span className="text-xs text-green-700 bg-green-50 px-2 py-0.5 rounded-full">Complete</span>
                  )}
                </div>
                <p className="text-sm text-gray-600 mt-0.5">{step.description}</p>
                {step.lastRun && (
                  <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                    <ClockIcon className="w-3.5 h-3.5" />
                    Last run: {formatLastRun(step.lastRun)}
                    {step.runCount > 1 ? ` (${step.runCount} times)` : ''}
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={() => setActiveStep(step.step)}
                className="flex-shrink-0 text-sm font-medium text-blue-600 hover:text-blue-800 px-3 py-1.5 rounded-lg hover:bg-blue-50"
              >
                {step.complete ? 'Run again' : 'Run'}
              </button>
            </div>
          </div>
        ))}
      </div>

      {wizardStep != null && status && (
        <div className="border-t border-gray-200 pt-8">
          <OnboardingWizard
            mode="settings"
            status={status}
            setStatus={setStatus}
            step={wizardStep}
            setStep={setActiveStep}
            onStepFinished={handleStepFinished}
            showStepper={false}
            compact
          />
        </div>
      )}
    </div>
  );
}
